import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UsersProvider } from '../../providers/users-provider';
import { LangsProvider } from "../../../common/providers/langs-provider";
import { HasPendingChangesGuard } from '../../../common/guards/has-pending-changes';
import { UsersChangePasswordDialogComponent } from '../../components/change-password-dialog/change-password-dialog';
import { BreadcrumbProvider } from '../../../breadcrumb/providers/breadcrumb-provider';
import moment from 'moment';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { User } from "../../../common/models/user";
import { Lang } from "../../../common/models/lang";
import "moment-timezone/index";
import { map } from "rxjs/operators";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { NgForOf, NgIf } from "@angular/common";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { MatFormFieldModule } from "@angular/material/form-field";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { InputImageComponent } from "../../../common/components/input-image/input-image";
import { MatButtonModule } from "@angular/material/button";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import {
  DiscussionParticipantDialogComponent
} from "../../../chat/participant/discussion-participant-dialog.component";
import { Organization } from "../../../common/models/organization";
import { DiscussionComponent } from "../../../chat/discussion/discussion.component";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { UsersChangeEmailDialogComponent } from "../../components/change-email-dialog/change-email-dialog";

@Component({
  selector: 'users-edit-page',
  providers: [],
  styleUrls: ['./edit.scss'],
  standalone: true,
  imports: [
    Error404Component,
    NgIf,
    PageContentComponent,
    HeaderComponent,
    MatFormFieldModule,
    ModuleContentComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    NgForOf,
    InputImageComponent,
    MatButtonModule,
    MainActionComponent,
    MatIconModule,
  ],
  templateUrl: './edit.html'
})
export class UsersEditPageComponent implements AfterViewInit, OnDestroy {
    public DESCRIPTION_MAX_LENGTH = 5000;
    public tzNames: string[];
    // public mainActions: ModuleMenu[] = [];
    public moreMenus: any[] = [];
    public hierarchy: any[] = [];
    public pageTitle = '';
    public isLoading = true;
    public isError404 = false;
    public isFromModerators = false;

    public firstNameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public lastNameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public emailFormControl: FormControl = new FormControl({value: '', disabled: true});
    public userLinkFormControls: FormControl[] = [];

    public user: User = {} as User;
    public avatarImage: any = {};
    public langs: Lang[] = [];
    public userLinksData: any[] = [];
    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        public usersProvider: UsersProvider,
        private _utils: UtilsProvider,
        private _title: Title,
        private _router: Router,
        private _route: ActivatedRoute,
        private _langs: LangsProvider,
        private _translater: TranslateService,
        private _toaster: MatSnackBar,
        private _changesGuard: HasPendingChangesGuard,
        private _changeDetector: ChangeDetectorRef,
        private dialog: MatDialog,
    ) {
        this.tzNames = moment.tz.names();
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:beforeunload')
    public doSomething() {
        return !this._changesGuard.hasChanges();
    }

    public ngAfterViewInit() {
        this.subscriptions.push(
            this._route.params.subscribe((params) => {
                this.initPage();
            })
        );
    }

    public onEmailChangeClicked(): void {
      const dialog: MatDialogRef<UsersChangeEmailDialogComponent> = this.dialog.open(UsersChangeEmailDialogComponent, {
          data: {
              user: this.user
          }
      });
    }

    public onSaveClicked(): Subscription|false {
      if (this.isFormValid()) {
          this.isLoading = true;
          return this.save().subscribe({
            next: (result) => {
              this.user = result[0];
              this.isLoading = false;
              this._changesGuard.unregisterChanges();

              if (this.currentUser.user!.id === this.user.id
                && (this.currentUser.user!.avatar !== this.user.avatar
                  || this.currentUser.user!.langId !== this.user.langId)) {
                window.location.href = this._route.snapshot.url.join('/') + '?' + this._utils.randomString(8);
              } else {
                this._toaster.open(this._translater.instant('User_saved'), '', {duration: 5000});
                this.initNav();
              }
              return true;
            },
            error: (error) => {
                this.onError(error);
                return false;
            }
          });
      } else {
          this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
          return false;
      }
    }

    public onFirstNameChange(): void {
        this.onFormChanged();
    }

    public onLastNameChange(): void {
        this.onFormChanged();
    }


    public onAvatarImageChange(): void {
        this.onFormChanged();
    }

    public onIntroChange(): void {
        this.onFormChanged();
        if (this.user.intro && this.user.intro.length > this.DESCRIPTION_MAX_LENGTH) {
            this.user.intro = this.user.intro.substr(0, this.DESCRIPTION_MAX_LENGTH);
        }
    }

    public onLangChange(): void {
        this.onFormChanged();
    }

    public onUserLinksChanged(): void {
        this.onFormChanged();
    }

    public onChangePasswordClicked(): void {
        const dialog: MatDialogRef<UsersChangePasswordDialogComponent> = this.dialog.open(UsersChangePasswordDialogComponent, {
            data: {
                user: this.user
            }
        });
    }

    public isFormValid(): boolean {
        if (this.firstNameFormControl.invalid
            || this.lastNameFormControl.invalid
        ) {
            return false;
        }

        for (let i = 0; i < this.userLinkFormControls.length; i++) {
            if (this.userLinkFormControls[i].invalid) {
                return false;
            }
        }

        return true;
    }

    private onFormChanged(): void {
        this._changesGuard.registerChanges(() => this.onSaveClicked());
    }

    private initPage(): void {
        this.loadData().subscribe({
            next: () => {
                this.initForms();
                this.initNav();
                this.isLoading = false;
            },
            error: (error) => {
                if (error.status === 404) {
                    this.isError404 = true;
                } else {
                    this.onError(error);
                }
            }
        })
        this._changeDetector.detectChanges();
    }

    private initNav(): void {
        /*
        this.mainActions = [
            {title: 'Save', icon: 'check', url: null, onClick: () => this.onSaveClicked()}
        ];
        */

        this.moreMenus = [
            { name: 'Change_password', link: '.', click: () => { this.onChangePasswordClicked(); } }
        ];

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private initForms(): void {
        this.userLinkFormControls = [];
        for (let i = 0; i < this.userLinksData.length; i++) {
            this.userLinkFormControls.push(new FormControl('', [
                (control: any) => {
                    if (!control.value || !this.userLinksData[i].userLink.matchWith) {
                        return null;
                    }

                    return control.value.startsWith(this.userLinksData[i].userLink.matchWith) ? null : { wrongLink: { valid: false } };
                }
            ]));
        }
    }

    private loadData(): Observable<any> {
            this.langs = this._langs.getLangs();
            const userId: number = this.parseUrl();
            if (userId) {
              return forkJoin([
                this.currentUser.getCurrentUser(),
                this.usersProvider.getUserUserLinksData(userId)
              ]).pipe(
                  map(([user, userLinksData]) => {
                      if (user) {
                          this.user = user;
                          this.avatarImage = { current: this.user.avatar, new: null };
                          this.userLinksData = userLinksData
                          return null;
                      } else {
                          return { status: 404 };
                      }
                  })
              );
            } else {
                return of({ status: 404 })
            }
    }

    private parseUrl(): number {
        let userId = 0;
        this.isFromModerators = false;
        const param1: any = this._route.snapshot.url[1].path;
        if (param1 === 'moderators') {
            userId = parseInt(this._route.snapshot.url[2].path, 10);
            this.isFromModerators = true;
        } else {
            userId = parseInt(param1, 10);
        }

        return userId;
    }

    private save(): Observable<any> {
        // on est obligé de cloner parce que sinon l'image qui est bindée
        // va devenir un fichier au lieu d'une url dans la barre de navigation
        if (this.avatarImage.new) {
            this.user.avatar = this.avatarImage.new;
        } else if (!this.avatarImage.current) {
            this.user.avatar = undefined;
        }

        return forkJoin([
          this.usersProvider.updateUser(this.user.id!, this.user),
          // TODO : CHECK CE QUE C'EST USERLINKS
          // this.usersProvider.saveUserLinks(this.user.id!, this._utils.getKeyValues(this.userLinksData, 'user_userLink'))
        ]);

    }

    private createPageTitle(){

        const userName: string = this.user.firstName + ' ' + this.user.lastName;
        this._title.setTitle(userName + ' | ' + this._translater.instant('My_account') + ' | ' + this.conf.platformName);
        this.pageTitle = 'Account_settings';
    }

    private createBreadcrumb() {
        this.breadcrumbProvider.setDefaultCrumbs([]);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            const details:any = [];
            if (error._body) {
                let details: any = JSON.parse(error._body);
                if (details.errors) {
                    details = details.errors;
                }
            }

            if (error.error && error.error.errors){
                if (error.error.errors.avatar) {
                    this._toaster.open(this._translater.instant(error.error.errors.avatar[0]), '', {duration: 5000});
                } else {
                    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
                }
            } else {
                this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
            }
        } else if (error.status === 404) {
            this._router.navigate(['404']);
        } else {
            this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
        }
    }
}
